import {
  NO_REASON_CODE,
  REASON_CODES,
  RUNBOOK_URL,
  type ReasonCodeSelection,
  isReasonCode,
} from '@/features/SalesforceIngest/lib/reasonCodes'
import {
  Link as ChakraLink,
  Field,
  Portal,
  Select,
  createListCollection,
} from '@chakra-ui/react'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Codes stay verbatim as both label and value. The operator is matching what
 * they can see in the Slack message, so prettifying `multi-auth0` into
 * "Multiple Auth0 accounts" would defeat the only thing this field is for.
 */
const REASON_CODE_COLLECTION = createListCollection({
  items: REASON_CODES.map((code) => ({ label: code, value: code })),
})

type Props = {
  value: ReasonCodeSelection
  onChange: (value: ReasonCodeSelection) => void
}

/**
 * Optional, and clearable.
 *
 * Optional because it is never sent, so blocking a submission on it would be
 * indefensible the first time an eleventh code appears that this list does
 * not enumerate. Clearable because the operator may pick the wrong code, and
 * a selection that hides the form with no way back is a trap.
 *
 * The helper is explicit that this is guidance and the runbook is the source
 * of truth. The form's summary of a case can go stale; the document the
 * needs-attention message already links to cannot.
 */
export function ReasonCodeSelect({ value, onChange }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const label = String(t('salesforceIngest.form.reasonCode.label'))

  return (
    <Field.Root>
      <Field.Label>{label}</Field.Label>
      <Select.Root
        collection={REASON_CODE_COLLECTION}
        aria-label={label}
        width="20rem"
        value={value === NO_REASON_CODE ? [] : [value]}
        onValueChange={(details) => {
          const [selected] = details.value
          onChange(
            selected && isReasonCode(selected) ? selected : NO_REASON_CODE,
          )
        }}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText
              placeholder={String(
                t('salesforceIngest.form.reasonCode.placeholder'),
              )}
            />
          </Select.Trigger>
          <Select.IndicatorGroup>
            {/* Only rendered once something is selected, so the operator can
                always get back to the full form. */}
            <Select.ClearTrigger
              aria-label={String(t('salesforceIngest.form.reasonCode.clear'))}
            />
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {REASON_CODE_COLLECTION.items.map((item) => (
                <Select.Item key={item.value} item={item}>
                  <Select.ItemText fontFamily="mono">
                    {item.label}
                  </Select.ItemText>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
      <Field.HelperText gap={1}>
        {String(t('salesforceIngest.form.reasonCode.helper'))}
        <ChakraLink href={RUNBOOK_URL} target="_blank" rel="noreferrer">
          {String(t('salesforceIngest.form.reasonCode.runbook'))}
          <ExternalLink size={12} />
        </ChakraLink>
      </Field.HelperText>
    </Field.Root>
  )
}
